import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import Asterisk from '../Asterisk/Asterisk';
import Dropdown, { Option } from '../Dropdown/Dropdown';
import { faTrashCan, faSortAsc, faSortDesc } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../Button/Button';
import DataTable from '../DataTable/DataTable';
import { useReadOnly } from '../../../utils/ReadOnlyContext';
import { defaultNumberPerPage } from '../../../assets/data/pagination/pagination';
import { exactMatcher, getFormErrorMessageByName, hasError, useFormErrorContext } from '../../../utils/context/FormErrorContext';
import { BaseAsyncReducerState } from '../../../redux/types';
import { useDispatch, useSelector } from 'react-redux';
import { resetDeleteIncomeUnitById } from '../../../redux/actions/incomeUnit/incomeUnit';
import { toast } from 'react-toastify';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { GetElsLookupsState } from '../../../redux/reducers/getLookups/getElsLookupsReducer';
import { getElsLookups } from '../../../redux/actions/lookups/lookups';
import AIRPNumberInput from '../AIRPNumberInput/AIRPNumberInput';
import { selectDeleteIncomeUnit, selectELSLookups, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';
import IncomeUnitType, { getDefaultUnit } from '../../../types/unit/IncomeUnit';
import Input from '../Input/Input';
import i18n from '../../../i18n';

interface SuiteBreakdownTableProps {
    data: IncomeUnitType[];
    setData: React.Dispatch<React.SetStateAction<IncomeUnitType[]>>;
    title: string;
    onChange: (e: React.ChangeEvent | number | null, index: number, key: string, isNumber?: boolean) => void;
    onSave?: (idx: number) => boolean;
    onDelete?: (idx: number) => void;
    isSuiteTypeMandatory?: boolean;
    isNumUnitsMandatory?: boolean;
    isNumBedsMandatory?: boolean;
    isNumVacantMandatory?: boolean;
    isAreaSFMandatory?: boolean;
    isActualRentMandatory?: boolean;
    isMarketRentMandatory?: boolean;
    isTypicalCareRateMandatory?: boolean;
    isSecondOccupantFeeMandatory?: boolean;
    unitTypeCode: '1' | '2' | '3' | '4' | '5' | '6';
    setIsRequestSent: (value: boolean) => void;
}

export type UnitDataWrapperType = IncomeUnitType & {
    new?: boolean;
    isErrorUnit?: boolean;
}

function SuiteBreakdownTable(props: SuiteBreakdownTableProps) {
    const deleteIncomeUnitState: BaseAsyncReducerState = useSelector(selectDeleteIncomeUnit);
    const saveResultState: SaveResultState = useSelector(selectSaveResult)
    const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups);
    const dispatch = useDispatch();
    const { t } = useTranslation(['suite_breakdown_table']);
    const [currentPage, setCurrentPage] = useState(1);
    const [numberPerPage, setNumberPerPage] = useState(defaultNumberPerPage);
    const { isReadOnly } = useReadOnly()
    // const [errorPageNums, setErrorPageNums] = useState<number[]>([])
    const { errors } = useFormErrorContext()
    const [deletedIdx, setDeletedIdx] = useState(-1);
    const [errorPageNums, setErrorPageNums] = useState<number[]>([])
    const [sortErrors, setSortErrors] = useState(false)
    const [hasMovedUserToPage1, setHasMovedUserToPage1] = useState(false)
    const [unitSubtype4Options, setUnitSubtype4Options] = useState<Option[]>([]);
    const isModifiedSubmission = sessionStorage.getItem("isInternalUser") && sessionStorage.getItem("editMode");
    const [currentSortingField, setCurrentSortingField] = useState({ "field": "", "type": "" });
    const [currentSortingOrder, setCurrentSortingOrder] = useState(2);
    const [selectedAnalysisValue, setSelectedAnalysisValue] = useState("")
    const getSortingIndicator = (field: string) => {
        if (field === currentSortingField.field) {
            if (currentSortingOrder === 1) {
                return <FontAwesomeIcon icon={faSortAsc} />
            } else if (currentSortingOrder === 0) {
                return <FontAwesomeIcon icon={faSortDesc} />
            }
        }
        return null;
    }

    const handleSort = (field: string, type: string) => {
        setCurrentSortingField({ "field": field, "type": type })
        const order = (currentSortingOrder + 1) % 3;
        setCurrentSortingOrder(order);
    }

    const formatOutput = (value: string | number | null | undefined) => {
        if (value === null || value === undefined) {
            return ""
        } else {
            return value.toString()
        }
    }

    const updateAllValues = (e: React.ChangeEvent) => {
        const analysisValue = (e.target as HTMLSelectElement).value;
        const analysisReviewedBy = localStorage.getItem("username") ?? "";
        const analysisReviewedDate = (new Date().toISOString());
        setSelectedAnalysisValue(analysisValue);
        if (props.data) {
            const updatedUnits: IncomeUnitType[] = props.data.map(unit => ({
                ...unit,
                analysisFlag: analysisValue,
                analysisReviewedBy: analysisReviewedBy,
                analysisReviewedDate: analysisReviewedDate
            }))
            setUnitDataWrapper(updatedUnits)
        }
    }

    let breakdownHeadings = [
        <th title={t("titleSuiteType")} className='pier-datatable-clear-th wider-column' key="column-1-header">{t("suiteBreakdownType")} {props.isSuiteTypeMandatory && <Asterisk />}</th>,
        <th title={t("titleUnits")} className='pier-datatable-clear-th' key="column-2-header">{t("suiteBreakdownUnits")} {props.isNumUnitsMandatory && <Asterisk />}</th>,
        <th className='pier-datatable-clear-th' key="column-3-header">{t("suiteBreakdownBeds")} {props.isNumBedsMandatory && <Asterisk />}</th>,
        <th className='pier-datatable-clear-th' key="column-4-header">{t("suiteBreakdownVacant")} {props.isNumVacantMandatory && <Asterisk />}</th>,
        <th title={t("titleSF")} className='pier-datatable-clear-th' key="column-5-header">{t("suiteBreakdownSf")} {props.isAreaSFMandatory && <Asterisk />}</th>,
        <th title={t("titleActualRent")} className='pier-datatable-clear-th' key="column-6-header">{t("suiteBreakdownActualRent")} {props.isActualRentMandatory && <Asterisk />}</th>,
        <th title={t("titleMarketRent")} className='pier-datatable-clear-th' key="column-7-header">{t("suiteBreakdownMarketRent")} {props.isMarketRentMandatory && <Asterisk />}</th>,
        <th title={t("titleTypicalCare")} className='pier-datatable-clear-th' key="column-8-header">{t("suiteBreakdownTypicalCare")} {props.isTypicalCareRateMandatory && <Asterisk />}</th>,
        <th title={t("titleSecondOcc")} className='pier-datatable-clear-th' key="column-9-header">{t("suiteBreakdownSecondOcc")} {props.isSecondOccupantFeeMandatory && <Asterisk />}</th>,
        <th className='pier-datatable-clear-th' key="column-10-header"></th>
    ]

    if (isModifiedSubmission) {
        breakdownHeadings = [
            <th onClick={() => handleSort("buildingIdNumber", "string")} className='internal-header small-header' key="column-00-header">Imp Seq # {getSortingIndicator("buildingIdNumber")}</th>,
            <th className='internal-header wider-analysis' key="column-01-header">
                <div className='inline-elements'>
                    <div onClick={() => handleSort("analysisFlag", "string")}>Analysis {getSortingIndicator("analysisFlag")}</div>
                    <Dropdown className='admin-input-style' name='analysisFlag' options={(elsLookupsState.dropdowns["rentAnalysisOptionsNoDesc"] ?? []) as Option[]} value={selectedAnalysisValue}
                        onChange={e => updateAllValues(e)} ariaLabel={`analysis-internal-set-all`} />
                </div>
            </th>,
        ].concat(breakdownHeadings)
    }

    const convertLookupsToDropdownOptions = (elsLookups: Record<string, unknown>[], valueKey: string, textKey: string, textKeyFr: string) => {
        const options: Option[] = elsLookups.map((lookup) => {
            return {
                value: lookup[valueKey] as string,
                text: i18n.language === "fr" ? lookup[textKeyFr] as string : lookup[textKey] as string
            }
        })
        return [{ value: "", text: "" }, ...options]
    }

    useEffect(() => {
        if (elsLookupsState.success) {
            const options = convertLookupsToDropdownOptions((elsLookupsState.dropdowns['unitSubType4'] as Record<string, unknown>[]), "unitSubtypeCode", "unitSubtypeDescription", "unitSubtypeDescriptionFr")
            setUnitSubtype4Options(options)
        } else if (!elsLookupsState.success && !elsLookupsState.loading) {
            const propCode = sessionStorage.getItem("propertyCode") ?? "";
            dispatch(getElsLookups(propCode))
        }
    }, [dispatch, elsLookupsState])

    const removeUnit = (e: React.MouseEvent, index: number) => {
        e.preventDefault();


        const dataSet = [...props.data]
        const unit = dataSet[index]

        if (unit['tenantId'] !== undefined) {
            setDeletedIdx(index);
            props.onDelete && props.onDelete(index);
        } else {
            dataSet.splice(index, 1)
            setUnitDataWrapper(dataSet)
        }

    }

    const isAnyFieldError = useCallback((unit: UnitDataWrapperType) => {
        const fields = ["unitSubtypeCode", "suiteBreakdownType", "tenantUnitNumber", "unitCount", "vacantUnitCount", "netLeasableArea", "monthlyActualRent", "monthlyMarketRent", "typicalCareRate", "secondOccupantFee"]
        return fields.some((field) => hasError(errors, unit.componentId + "." + field, exactMatcher))
    }, [errors])

    const updateErrorPages = useCallback((units: UnitDataWrapperType[]) => {
        const errorPages = []
        const totalPages = Math.ceil(units.length / numberPerPage)
        for (let page = 0; page < totalPages; page++) {
            const start = page * numberPerPage
            const end = Math.min(start + numberPerPage, units.length)

            for (let i = start; i < end; i++) {
                if (isAnyFieldError(units[i])) {
                    errorPages.push(page + 1)
                    break
                }
            }
        }
        setErrorPageNums(errorPages)
    }, [numberPerPage, isAnyFieldError])

    const compareValues = (a: UnitDataWrapperType, b: UnitDataWrapperType, field: string, type: string, order: 'asc' | 'desc') => {
        const valueA = a[field as keyof UnitDataWrapperType]
        const valueB = b[field as keyof UnitDataWrapperType]

        if (valueA === null && valueB === null) return 0
        if (valueA === null) return order === 'asc' ? -1 : 1
        if (valueB === null) return order === 'asc' ? 1 : -1

        let result = 0

        switch (type) {
            case 'string':
                result = String(valueA).localeCompare(String(valueB))
                break
            case 'number':
                result = Number(valueA) - Number(valueB)
                break
            case 'bool':
                result = (valueA === valueB) ? 0 : valueA ? 1 : -1
                break
            case 'array': {
                const lengthA = Array.isArray(valueA) ? valueA.length : 0
                const lengthB = Array.isArray(valueB) ? valueB.length : 0
                result = lengthA - lengthB
                break;
            }
        }
        return order === 'desc' ? -result : result
    }

    const setUnitDataWrapper = useCallback((modifiedUnits: UnitDataWrapperType[]) => {
        const unitsWithComponentId = modifiedUnits.map(unit => unit.componentId ? unit : { ...unit, componentId: String(crypto.randomUUID()) })
        const sorted = [...unitsWithComponentId].sort((a, b) => {
            const isANew = a.new === true;
            const isBNew = b.new === true;
            if (isANew !== isBNew) {
                return isANew ? -1 : 1;
            }

            const isAError = isAnyFieldError(a) || a.isErrorUnit === true
            const isBError = isAnyFieldError(b) || b.isErrorUnit === true
            const isANewAndNoError = !isAError && isANew
            const isBNewAndNoError = !isBError && isBNew

            if (isANewAndNoError !== isBNewAndNoError) {
                return isANewAndNoError ? -1 : 1
            }

            if (isAError !== isBError) {
                return isAError ? -1 : 1
            }

            if (currentSortingOrder === 1) { // ASC
                return compareValues(a, b, currentSortingField.field, currentSortingField.type, "asc")
            } else if (currentSortingOrder === 0) { // DESC
                return compareValues(a, b, currentSortingField.field, currentSortingField.type, "desc")
            }

            const tenantIdA = Number(a.tenantId) || 0;
            const tenantIdB = Number(b.tenantId) || 0;

            return tenantIdB - tenantIdA
        })

        props.setData(sorted)
    }, [isAnyFieldError, props, currentSortingOrder, currentSortingField])


    useEffect(() => {
        updateErrorPages(props.data)
    }, [props.data, updateErrorPages])

    useEffect(() => {
        // Update sorting
        if (props.data) {
            setUnitDataWrapper(props.data)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSortingOrder, currentSortingField])


    useEffect(() => {
        if (errors.length > 0 && sortErrors) {
            if (errorPageNums.length === 1 && !hasMovedUserToPage1) {
                setCurrentPage(errorPageNums[0])
                setHasMovedUserToPage1(true)
            }
            const updatedUnits = props.data.map((unit) => isAnyFieldError(unit) ? { ...unit, "isErrorUnit": true } : unit)
            setSortErrors(false)
            setUnitDataWrapper(updatedUnits)
        }
    }, [setSortErrors, sortErrors, errors, setUnitDataWrapper, isAnyFieldError, errorPageNums, props.data, hasMovedUserToPage1])

    useEffect(() => {
        if (saveResultState.success) {
            const respData = saveResultState.data as unknown as IncomeUnitType[] || []
            if (respData.length > 0) {
                setUnitDataWrapper(respData)
            }
            setHasMovedUserToPage1(false)
            setSortErrors(true)
        }
    }, [saveResultState, props, setUnitDataWrapper])

    useEffect(() => {
        if (deleteIncomeUnitState.success) {
            dispatch(resetDeleteIncomeUnitById())
            const dataSet = [...props.data]
            dataSet.splice(deletedIdx, 1)
            setUnitDataWrapper(dataSet)

            //setIsRequestSent(false)
            toast.success(t("data-deleted") as string)
            setDeletedIdx(-1);
        }
    }, [deleteIncomeUnitState.success, dispatch, t, setUnitDataWrapper, props.data, deletedIdx]);

    useEffect(() => {
        if (props.data.length > 0) {
            const analysisFlagToCompare = props.data[0]["analysisFlag"];
            if (analysisFlagToCompare) {
                const allAnalysisEqual = props.data.every(t => t.analysisFlag === analysisFlagToCompare);
                allAnalysisEqual ? setSelectedAnalysisValue(analysisFlagToCompare === null ? "" : (analysisFlagToCompare as string)) : setSelectedAnalysisValue("");
            }
        }
    }, [props.data])

    const addUnit = (e: React.MouseEvent) => {
        e.preventDefault();
        let newUnit: UnitDataWrapperType;
        if (props.unitTypeCode) {
            newUnit = {
                ...getDefaultUnit(),
                new: true,
                unitTypeCode: props.unitTypeCode,
            }
        } else {
            newUnit = {
                ...getDefaultUnit(),
                new: true
            }
        }
        const updatedData = [newUnit, ...props.data]
        setUnitDataWrapper(updatedData);
    }

    const updateAnalysis = (e: React.ChangeEvent, index: number, key: string) => {
        const value = ((e as React.ChangeEvent).target as HTMLSelectElement).value
        if (value !== selectedAnalysisValue) {
            setSelectedAnalysisValue("")
        }
        props.onChange(e, index, key)
    }

    const buildTableContent = (): JSX.Element => {
        const tableBody: JSX.Element[] = [];

        // Helper function to check if the value is negative
        // const isNegative = (value: string | number | boolean) => {
        //     if (typeof value === "number" && value < 0){
        //         return true;
        //     }
        //     else if (typeof value === "string" && Number(value)<0){
        //         return true;
        //     }
        //     else {
        //         return false;
        //     }
        // };

        for (let index = numberPerPage * (currentPage - 1); index < Math.min(props.data.length, numberPerPage * currentPage); index++) {
            tableBody.push(
                <tr key={`table-row-${props.data[index].componentId}`}>
                    {(isModifiedSubmission) && <>
                        <td>
                            <Input name={`bin-${index}`}
                                id={`bin-${index}`} type="text" value={formatOutput(props.data[index].buildingIdNumber as string)} onChange={e => props.onChange(e, index, "buildingIdNumber")} ariaLabel={`bin-${index}`} />
                        </td>
                        <td>
                            <Dropdown name='analysisFlag' options={(elsLookupsState.dropdowns["rentAnalysisOptionsNoDesc"] ?? []) as Option[]} value={formatOutput(props.data[index].analysisFlag as string | null)}
                                onChange={e => updateAnalysis(e, index, "analysisFlag")} ariaLabel={`analysis-${index}`} />
                        </td>
                    </>}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".unitSubtypeCode", exactMatcher) ? "table-error" : ""}`}>
                        <Dropdown
                            name='unitSubtypeCode'
                            options={unitSubtype4Options}
                            value={props.data[index].unitSubtypeCode ? props.data[index].unitSubtypeCode as string : ""}
                            onChange={e => props.onChange(e, index, "unitSubtypeCode")} ariaLabel={t("unitSubtypeCode") + " " + index}
                            hideErrorCross
                            errorMessage={getFormErrorMessageByName(errors, props.data[index].componentId as string + ".unitSubtypeCode", exactMatcher)} />
                    </td>
                    {/* Number of Units */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".tenantUnitNumber", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            name={props.data[index].componentId as string + ".tenantUnitNumber"}
                            initialValue={props.data[index].tenantUnitNumber ? props.data[index].tenantUnitNumber as string : ""}
                            onChange={(value: number | null) => props.onChange(value, index, "tenantUnitNumber", true)}
                            errorMatcher={exactMatcher}
                            decimals={false} />
                    </td>
                    {/* Number of Beds */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".unitCount", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            name={props.data[index].componentId as string + ".unitCount"}
                            initialValue={props.data[index].unitCount ? props.data[index].unitCount : ""}
                            onChange={(value: number | null) => props.onChange(value, index, "unitCount", true)}
                            errorMatcher={exactMatcher}
                            decimals={false} />
                    </td>
                    {/* Number of Vacant */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".vacantUnitCount", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            name={props.data[index].componentId as string + ".vacantUnitCount"}
                            initialValue={props.data[index].vacantUnitCount ? props.data[index].vacantUnitCount : ""}
                            onChange={(value: number | null) => props.onChange(value, index, "vacantUnitCount", true)}
                            errorMatcher={exactMatcher}
                            decimals={false} />
                    </td>
                    {/* Area in SF */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".netLeasableArea", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            formatType="integer"
                            name={props.data[index].componentId as string + ".netLeasableArea"}
                            initialValue={props.data[index].netLeasableArea ? props.data[index].netLeasableArea : ""}
                            onChange={(value: number | null) => props.onChange(value, index, "netLeasableArea", true)}
                            errorMatcher={exactMatcher} />
                    </td>
                    {/* Actual Rent */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".monthlyActualRent", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            symbol='$'
                            name={props.data[index].componentId as string + ".monthlyActualRent"}
                            initialValue={props.data[index].monthlyActualRent ? props.data[index].monthlyActualRent : ""}
                            decimals={true}
                            decimalPlaces={2}
                            onChange={(value: number | null) => props.onChange(value, index, "monthlyActualRent", true)}
                            errorMatcher={exactMatcher} />
                    </td>
                    {/* Market Rent */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".monthlyMarketRent", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            symbol='$'
                            name={props.data[index].componentId as string + ".monthlyMarketRent"}
                            initialValue={props.data[index].monthlyMarketRent ? props.data[index].monthlyMarketRent : ""}
                            onChange={(value: number | null) => props.onChange(value, index, "monthlyMarketRent", true)}
                            errorMatcher={exactMatcher} />
                    </td>
                    {/* Typical Care Rate */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".typicalCareRate", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            symbol='$'
                            name={props.data[index].componentId as string + ".typicalCareRate"}
                            initialValue={props.data[index].typicalCareRate ? props.data[index].typicalCareRate : ""}
                            onChange={(value: number | null) => props.onChange(value, index, "typicalCareRate", true)}
                            errorMatcher={exactMatcher} />
                    </td>
                    {/* Second Occupant Fee */}
                    <td className={`${hasError(errors, props.data[index].componentId as string + ".secondOccupantFee", exactMatcher) ? "table-error" : ""}`}>
                        <AIRPNumberInput
                            symbol='$'
                            name={props.data[index].componentId as string + ".secondOccupantFee"}
                            initialValue={props.data[index].typicalCareRate ? props.data[index].secondOccupantFee : ""}
                            onChange={(value: number | null) => props.onChange(value, index, "secondOccupantFee", true)}
                            errorMatcher={exactMatcher} />
                    </td>
                    <td>
                        <Button disabled={isReadOnly} onClick={(e) => removeUnit(e, index)} text={<FontAwesomeIcon icon={faTrashCan} className={isReadOnly ? "trash-off" : "trash"} />} ariaLabel={t("remove") + " " + index} />
                    </td>
                </tr>
            );
        }

        return (<>{tableBody}</>);
    };


    const tableTitle = <div className="pier-datatable-title-container">
        <span>{t(props.title)}</span>
        <p className='suite-breakdown-desc'>{t("suiteBreakdownDesc")}</p>
    </div>

    return (
        <>
            <div className='assisted-suite-breakdown-header-row'>
                <div className='suite-breakdown-btn-container'>
                    {!isReadOnly && <Button type="submit" onClick={e => addUnit(e)} text={t("add") as string} />}
                </div>
            </div>

            <DataTable
                totalPages={Math.ceil(props.data.length / numberPerPage)}
                titleEle={tableTitle}
                headings={breakdownHeadings}
                className={"assisted-suite-breakdown-table" + (isModifiedSubmission ? " assisted-suite-breakdown-table-internal" : "")}
                currentPage={currentPage}
                changePage={setCurrentPage}
                numberPerPage={numberPerPage}
                changeNumberPerPage={setNumberPerPage}
                errorPages={errorPageNums}>
                {buildTableContent()}
            </DataTable>
        </>
    )
}

export default SuiteBreakdownTable
